class TextMarquee extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
    this.resizeObserver = null;
    this.fontsReady = false;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.speed = parseFloat(this.dataset.speed) || 40;
    this.direction = this.dataset.direction || 'left';
    this.pauseOnHover = this.dataset.pauseHover === 'true';
    this.itemCount = parseInt(this.dataset.itemCount) || 0;

    if (this.itemCount < 1) {
      return;
    }

    this.waitForDependencies();
  }

  disconnectedCallback() {
    this.destroy();
  }

  waitForDependencies() {
    let gsapLoaded = false;

    const checkDeps = setInterval(() => {
      if (typeof gsap !== 'undefined') {
        gsapLoaded = true;
        clearInterval(checkDeps);
        this.waitForFonts().then(() => this.init());
      }
    }, 50);

    setTimeout(() => {
      if (!gsapLoaded) {
        clearInterval(checkDeps);
        console.error('TextMarquee: Timeout waiting for GSAP');
      }
    }, 5000);
  }

  waitForFonts() {
    if (document.fonts && document.fonts.ready) {
      return document.fonts.ready.then(() => {
        this.fontsReady = true;
      });
    }
    return Promise.resolve();
  }

  init() {
    this.cacheElements();
    if (!this.track || !this.list) {
      console.error('TextMarquee: Could not find track or list elements');
      return;
    }
    this.duplicateList();
    this.ensureFill();
    this.calculateDimensions();
    this.createAnimation();
    this.bindEvents();
    this.setupResizeObserver();
  }

  cacheElements() {
    this.track = this.querySelector('.text-marquee__track');
    this.list = this.querySelector('.text-marquee__list');
  }

  duplicateList() {
    const clone = this.list.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.add('is-clone');
    this.track.appendChild(clone);
  }

  ensureFill() {
    const viewportWidth = this.offsetWidth;
    const listWidth = this.list.offsetWidth;

    if (listWidth === 0 || viewportWidth === 0) return;

    const copiesNeeded = Math.ceil((viewportWidth * 2) / listWidth);
    const currentCopies = this.track.querySelectorAll('.text-marquee__list').length;

    for (let i = currentCopies; i < copiesNeeded; i++) {
      const clone = this.list.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('is-clone');
      this.track.appendChild(clone);
    }
  }

  calculateDimensions() {
    const trackStyle = getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.gap) || 0;
    this.listWidth = this.list.offsetWidth + gap;
    this.duration = this.listWidth / this.speed;
  }

  createAnimation() {
    if (this.animation) {
      this.animation.kill();
    }

    const isLeft = this.direction === 'left';
    const startX = isLeft ? 0 : -this.listWidth;
    const endX = isLeft ? -this.listWidth : 0;

    gsap.set(this.track, { x: startX });

    this.animation = gsap.to(this.track, {
      x: endX,
      duration: this.duration,
      ease: 'none',
      repeat: -1
    });
  }

  bindEvents() {
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());
    }

    document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
    document.addEventListener('shopify:section:unload', this.handleSectionUnload.bind(this));
  }

  handleSectionLoad(e) {
    if (e.detail.sectionId === this.sectionId) {
      this.destroy();
      this.connectedCallback();
    }
  }

  handleSectionUnload(e) {
    if (e.detail.sectionId === this.sectionId) {
      this.destroy();
    }
  }

  setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    let resizeTimeout;
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!this.track || !this.list) return;
        this.ensureFill();
        this.calculateDimensions();
        this.createAnimation();
      }, 150);
    });

    this.resizeObserver.observe(this);
  }

  pause() {
    if (this.animation) this.animation.pause();
  }

  resume() {
    if (this.animation) this.animation.resume();
  }

  destroy() {
    if (this.animation) {
      this.animation.kill();
      this.animation = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    const clones = this.querySelectorAll('.is-clone');
    clones.forEach(clone => clone.remove());
  }
}

if (!customElements.get('text-marquee')) {
  customElements.define('text-marquee', TextMarquee);
}
